import { Link } from 'react-router-dom';

interface PostCardProps {
  post: {
    id: string;
    title: string;
    excerpt: string;
    cover: string;
    channelName: string;
    channel: string;
    author: string;
    date: string;
    readMinutes: number;
    likes: number;
    comments: number;
  };
  variant?: 'default' | 'wide' | 'compact';
}

export default function PostCard({ post, variant = 'default' }: PostCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex gap-4 rounded-xl border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/60"
      >
        <div className="h-20 w-24 shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-[88px] sm:w-[112px]">
          <img
            src={post.cover}
            alt={`${post.title} - 糖心吃瓜网配图`}
            title={`${post.title} 糖心吃瓜网`}
            className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-between">
          <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
            {post.title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="text-primary-600">{post.channelName}</span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-line w-3.5 h-3.5 flex items-center justify-center" />
              {post.likes}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <article className="group relative overflow-hidden rounded-2xl border border-background-200/70 bg-background-50">
        <Link to={`/post/${post.id}`} className="block">
          <div className="h-64 w-full overflow-hidden bg-background-200 md:h-80">
            <img
              src={post.cover}
              alt={`${post.title} - 糖心吃瓜网热图`}
              title={`${post.title} 糖心吃瓜网`}
              className="h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/40 to-transparent p-6 pt-20 md:p-8 md:pt-28">
            <span className="inline-flex items-center rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              {post.channelName}
            </span>
            <h3 className="mt-3 font-heading text-xl text-background-50 md:text-3xl">
              {post.title}
            </h3>
            <p className="mt-2 hidden max-w-2xl text-sm text-background-200 md:block">
              {post.excerpt}
            </p>
          </div>
        </Link>
        <div className="flex items-center gap-4 border-t border-background-200/70 px-6 py-3 text-xs text-foreground-500 md:px-8">
          <span>{post.author}</span>
          <span>{post.date}</span>
          <span className="flex items-center gap-1">
            <i className="ri-heart-line w-3.5 h-3.5 flex items-center justify-center" />
            {post.likes}
          </span>
          <span className="ml-auto flex items-center gap-1">
            <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center" />
            {post.readMinutes} 分钟
          </span>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/post/${post.id}`} className="flex h-full flex-col">
        <div className="h-44 w-full overflow-hidden bg-background-200 sm:h-48">
          <img
            src={post.cover}
            alt={`${post.title} - 糖心吃瓜网内容封面`}
            title={`${post.title} 糖心吃瓜网`}
            className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
              {post.channelName}
            </span>
            <span className="text-xs text-foreground-400">{post.date}</span>
          </div>
          <h3 className="mt-3 line-clamp-2 text-base font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
            {post.title}
          </h3>
          <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
            {post.excerpt}
          </p>
          <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line w-3.5 h-3.5 flex items-center justify-center" />
              {post.author}
            </span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <i className="ri-heart-line w-3.5 h-3.5 flex items-center justify-center" />
                {post.likes}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line w-3.5 h-3.5 flex items-center justify-center" />
                {post.comments}
              </span>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}